<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';
import { useMediaQuery } from '@vueuse/core';
import { useGlobalStore } from '@/stores/global';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { EffectCoverflow, Pagination, Navigation } from 'swiper/modules';

import 'swiper/css';

import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import '@/assets/styles/swiper-custom.scss';

const globalStore = useGlobalStore();

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  isCatalog: {
    type: Boolean,
    default: false,
  },
});
const activeIndex = ref(0);
const onSlideChange = (swiper) => {
  activeIndex.value = swiper.realIndex;
};
const isMobile = useMediaQuery('(max-width: 767px)');

const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  items,
  button,
  hasTitle,
  hasItems,
  hasButton,
  template,
  hasAnyContent,
} = useBlockSafe(props.data);
const formatPrice = (price) => {
  return price.toLocaleString('uk-UA');
};
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div
        class="cars-popular"
        :class="[
          template ? `cars-popular--${template}` : '',
          backgroundColor === 'black' ? 'white' : 'black',
        ]"
      >
        <div
          class="cars-popular__top"
          :class="[hasButton ? `cars-popular__top--with-button` : '']"
          data-aos="fade-in"
          data-aos-delay="300"
        >
          <template v-if="hasTitle">
            <h1 v-if="template === 'hero'" class="mb-0 cars-popular__title h1">
              <template v-if="isCatalog && globalStore.catalogPageTitle">
                {{ globalStore.catalogPageTitle }}
              </template>
              <template v-else>
                {{ title }}
              </template>
            </h1>
            <h2 v-else class="mb-0 cars-popular__title h2" v-html="title"></h2>
          </template>
          <CTagDetect
            v-if="hasButton"
            :to="button?.url"
            :class="[
              'cars-popular__button p-button-text button-icon-next',
              backgroundColor === 'black' ? 'white' : 'black',
            ]"
            >{{ button?.title }}
            <CIcon
              class="button-icon-next-icon"
              name="arrow-red"
              width="16"
              height="16"
            />
          </CTagDetect>
        </div>
        <div v-if="hasItems" class="cars-popular__bottom">
          <div class="cars-popular__slider">
            <template v-if="isMobile">
              <Swiper
                :effect="'coverflow'"
                :coverflow-effect="{
                  rotate: 0,
                  stretch: 0,
                  depth: 130,
                  scale: 0.9,
                  modifier: 1,
                  slideShadows: false,
                }"
                :grab-cursor="true"
                :centered-slides="true"
                :slides-per-view="'auto'"
                :space-between="16"
                :initial-slide="0"
                :loop="true"
                :modules="[EffectCoverflow, Pagination]"
                :pagination="{
                  el: '.cars-popular__pagination',
                  type: 'bullets',
                }"
                @slide-change="onSlideChange"
              >
                <SwiperSlide
                  v-for="(item, itemIndex) in items"
                  :key="`cars-popular-${itemIndex}`"
                  class="cars-popular__slide"
                >
                  <div
                    v-if="item?.image?.src || item?.brand.src"
                    :class="[
                      'cars-popular__image-wrap',
                      !item?.image?.src
                        ? 'cars-popular__image-wrap--brand'
                        : '',
                    ]"
                  >
                    <NuxtImg
                      :src="item?.image?.src || item?.brand.src"
                      :alt="item?.image?.alt || item?.brand.alt"
                      :height="item?.image?.height || item?.brand.height"
                      :width="item?.image?.width || item?.brand.width"
                      :class="[
                        'cars-popular__image',
                        !item?.image?.src ? 'cars-popular__image--brand' : '',
                      ]"
                      data-aos="fade-left"
                      data-aos-delay="500"
                      loading="lazy"
                    />
                  </div>
                </SwiperSlide>
              </Swiper>
            </template>
            <template v-else>
              <Swiper
                :effect="'coverflow'"
                :coverflow-effect="{
                  rotate: 0,
                  stretch: 0,
                  depth: 310,
                  scale: 0.71,
                  modifier: 1,
                  slideShadows: false,
                }"
                :grab-cursor="true"
                :centered-slides="true"
                :slides-per-view="'auto'"
                :space-between="24"
                :initial-slide="0"
                :loop="true"
                :modules="[EffectCoverflow, Pagination, Navigation]"
                :navigation="{
                  prevEl: '.cars-popular__navigation-prev',
                  nextEl: '.cars-popular__navigation-next',
                }"
                :pagination="{
                  el: '.cars-popular__pagination',
                  type: 'fraction',
                  formatFractionCurrent: function (number) {
                    return number < 10 ? '0' + number : number;
                  },
                  formatFractionTotal: function (number) {
                    return number < 10 ? '0' + number : number;
                  },
                }"
                :breakpoints="{
                  1024: {
                    centeredSlides: false,
                    spaceBetween: 184,
                    coverflowEffect: {
                      depth: 500,
                      scale: 0.75,
                    },
                  },
                }"
                @slide-change="onSlideChange"
              >
                <SwiperSlide
                  v-for="(item, itemIndex) in items"
                  :key="`cars-popular-${itemIndex}`"
                  class="cars-popular__slide"
                >
                  <div
                    v-if="item?.image?.src || item?.brand.src"
                    :class="[
                      'cars-popular__image-wrap',
                      !item?.image?.src
                        ? 'cars-popular__image-wrap--brand'
                        : '',
                    ]"
                  >
                    <NuxtImg
                      :src="item?.image?.src || item?.brand.src"
                      :alt="item?.image?.alt || item?.brand.alt"
                      :height="item?.image?.height || item?.brand.height"
                      :width="item?.image?.width || item?.brand.width"
                      :class="[
                        'cars-popular__image',
                        !item?.image?.src ? 'cars-popular__image--brand' : '',
                      ]"
                      data-aos="fade-left"
                      data-aos-delay="500"
                      loading="lazy"
                    />
                  </div>
                </SwiperSlide>
              </Swiper>
            </template>
          </div>
          <div
            class="cars-popular__brand"
            data-aos="fade-left"
            data-aos-delay="400"
          >
            <NuxtImg
              v-if="items[activeIndex]?.brand?.src"
              :src="items[activeIndex].brand?.src"
              :alt="items[activeIndex].brand?.alt"
              :width="items[activeIndex].brand?.width"
              :height="items[activeIndex].brand?.height"
              fit="cover"
              position="center"
              class="cars-popular__brand-logo"
              loading="lazy"
            />
            <p
              class="mb-0 cars-popular__brand-title h4"
              v-html="items[activeIndex].title"
            ></p>
          </div>
          <div
            class="cars-popular__info"
            data-aos="fade-in"
            data-aos-delay="300"
          >
            <div class="cars-popular__info-content">
              <div
                v-if="items[activeIndex].in_stock"
                class="cars-popular__info-instock"
              >
                <CIcon
                  :name="backgroundColor === 'black' ? 'check-white' : 'check'"
                  width="12"
                  height="12"
                  class="cars-popular__info-instock-icon"
                />
                <span class="body-3-medium">{{ $t('car.available') }}</span>
              </div>
              <div class="cars-popular__info-price">
                <div class="cars-popular__info-price-item">
                  <span class="cars-popular__info-price-current h6">
                    <template v-if="items[activeIndex].pricing?.price_current">
                      <template v-if="!items[activeIndex].pricing?.discount">{{
                        $t('car.from')
                      }}</template>
                      {{
                        formatPrice(items[activeIndex].pricing?.price_current)
                      }}
                      {{ items[activeIndex].pricing?.currency }}
                    </template>
                    <template v-else>{{ $t('car.price_empty') }}</template>
                  </span>
                </div>
                <div
                  v-if="items[activeIndex].pricing?.discount"
                  class="cars-popular__info-price-item"
                >
                  <span
                    v-if="items[activeIndex].pricing?.price_old"
                    class="cars-popular__info-price-original label"
                    >{{ formatPrice(items[activeIndex].pricing?.price_old) }}
                    {{ items[activeIndex].pricing?.currency }}</span
                  >
                  <span
                    v-if="items[activeIndex].pricing?.discount"
                    class="cars-popular__info-discount body-3-medium"
                    >-{{ items[activeIndex].pricing?.discount }}%</span
                  >
                </div>
              </div>
              <div
                class="cars-popular__info-description body-3-regular max-w-400"
                v-html="items[activeIndex].short_description"
              ></div>
            </div>
            <Button v-slot="slotProps" as-child rounded>
              <CTagDetect
                :to="items[activeIndex].url"
                :class="[
                  slotProps.class,
                  'cars-popular__info-button link-normal',
                  backgroundColor === 'black' ? 'button-white' : 'button-black',
                  ' js-button-cars-popular',
                ]"
                >{{ $t('car.more_details') }}</CTagDetect
              >
            </Button>
          </div>
          <div
            class="swiper-pagination cars-popular__pagination"
            data-aos="fade-in"
            data-aos-delay="300"
          ></div>
          <div
            class="swiper-navigation cars-popular__navigation"
            data-aos="fade-in"
            data-aos-delay="300"
          >
            <Button
              type="button"
              rounded
              variant="outlined"
              class="cars-popular__navigation-prev button-prev js-button-cars-popular-prev"
              :aria-label="$t('slider.prev')"
            >
              <CIcon
                class="button-prev__icon"
                :name="
                  backgroundColor === 'black'
                    ? 'direction-white'
                    : 'direction-black'
                "
                width="16"
                height="16"
              />
            </Button>
            <Button
              type="button"
              rounded
              variant="outlined"
              class="cars-popular__navigation-next button-next js-button-cars-popular-next"
              :aria-label="$t('slider.next')"
            >
              <CIcon
                class="button-next__icon"
                :name="
                  backgroundColor === 'black'
                    ? 'direction-white'
                    : 'direction-black'
                "
                width="16"
                height="16"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.cars-popular {
  $parent: &;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 40px;
  @include media-breakpoint-up(sm) {
    gap: 64px;
  }
  @include media-breakpoint-up(md) {
    align-items: flex-start;
    gap: 80;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__top {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    justify-items: center;
    width: 100%;
    grid-template-columns: auto auto;
    justify-content: space-between;
    @include media-breakpoint-up(sm) {
      gap: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__title {
    margin: 0;
  }
  &__button {
    grid-template-columns: min-content 16px;
    @include media-breakpoint-up(sm) {
      grid-template-columns: auto 16px;
    }
  }
  &__bottom {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 24px;
    position: relative;
    @include media-breakpoint-up(sm) {
      gap: 32px;
    }
    @include media-breakpoint-up(md) {
      align-items: start;
      display: grid;
      gap: 0;
      grid-template-columns: 2.5fr 7fr 2.5fr;
      grid-template-areas: 'pagination gallery gallery' 'brand gallery gallery' 'info info navigation';
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 32);
    }
  }
  &__slider {
    margin-left: -16px;
    position: relative;
    width: calc(100% + 32px);
    &:before {
      content: '';
      //background-color: $divider;
      right: 0;
      position: absolute;
      top: 50%;
      height: 1px;
      width: 100vw;
      z-index: -1;
    }
    @include media-breakpoint-up(sm) {
      margin-left: -20px;
      width: calc(100% + 40px);
    }
    @include media-breakpoint-up(md) {
      grid-area: gallery;
      margin-left: 0px;
      width: calc(100% + 48px);
    }
    @include media-breakpoint-up(xl) {
      width: calc(100% + px-to-vw(48));
      &:before {
        @include responsive-spacing(height, 1);
      }
    }
    .swiper-slide {
      margin-bottom: auto;
      margin-top: auto;
      width: 67%;
      @include media-breakpoint-up(sm) {
        width: 76%;
      }
      @include media-breakpoint-up(md) {
        width: 69%;
      }
    }
  }
  &__brand {
    align-items: center;
    display: flex;
    flex-direction: column;
    margin-top: -16px;
    @include media-breakpoint-up(md) {
      align-items: flex-start;
      gap: 48px;
      grid-area: brand;
      margin-top: 0;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
    &-logo {
      height: auto;
      width: 64px;
      @include media-breakpoint-up(sm) {
        width: 96px;
      }
      @include media-breakpoint-up(lg) {
        @include responsive-width(96);
      }
    }
    &-title {
      @include media-breakpoint-up(sm) {
        margin-top: -6px;
      }
      @include media-breakpoint-up(md) {
        margin-top: 0;
      }
    }
  }
  &__info {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 24px;
    @include media-breakpoint-up(sm) {
      gap: 56px;
    }
    @include media-breakpoint-up(md) {
      align-items: flex-end;
      gap: 0;
      grid-area: info;
      flex-direction: row;
    }
    &-content {
      align-items: center;
      display: flex;
      flex-direction: column;
      gap: 16px;
      @include media-breakpoint-up(sm) {
        gap: 24px;
      }
      @include media-breakpoint-up(md) {
        align-items: flex-start;
        width: 27%;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 24);
      }
    }
    &-instock {
      align-items: center;
      border: 1px solid $divider;
      border-radius: 2px;
      display: grid;
      grid-template-columns: 12px auto;
      gap: 8px;
      padding: 2px 8px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-width, 1);
        @include responsive-spacing(border-radius, 2);
        @include responsive-spacing(gap, 8);
        @include responsive-spacing(padding-top, 2);
        @include responsive-spacing(padding-bottom, 2);
        @include responsive-spacing(padding-left, 8);
        @include responsive-spacing(padding-right, 8);
        grid-template-columns: px-to-vw(12) auto;
      }
      &-icon {
        height: auto;
        width: 100%;
      }
    }
    &-price {
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: 16px;
      justify-content: center;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
      }
      &-item {
        align-items: center;
        display: flex;
        flex-direction: row;
        gap: 8px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 8);
        }
      }
      &-original {
        color: $on-surface-variant-2;
        text-decoration: line-through;
      }
    }
    &-discount {
      border-radius: 2px;
      background-color: $primary;
      color: $white;
      padding: 5px 5px 1px 4px;
      position: relative;
      z-index: 1;
      &:before {
        background-color: inherit;
        content: '';
        height: 6px;
        left: -1px;
        position: absolute;
        top: calc(50% - 3px);
        transform: rotate(45deg);
        width: 6px;
        z-index: -1;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 2);
        @include responsive-spacing(padding-top, 5);
        @include responsive-spacing(padding-right, 5);
        @include responsive-spacing(padding-bottom, 1);
        @include responsive-spacing(padding-left, 4);
        &:before {
          @include responsive-spacing(height, 6);
          @include responsive-spacing(left, -1);
          @include responsive-spacing(width, 6);
          top: calc(50% - px-to-vw(3));
        }
      }
    }
    &-description {
      text-align: center;
      @include media-breakpoint-up(md) {
        text-align: left;
      }
      &:deep(p:last-child) {
        margin: 0;
      }
    }
    &-button {
      @include media-breakpoint-up(md) {
        margin: 0 auto;
      }
    }
  }
  &__pagination {
    padding-top: 16px;
    position: static;
    @include media-breakpoint-up(sm) {
      bottom: 14px;
      left: 0px;
      padding-top: 0;
      position: absolute;
      top: unset;
      width: auto;
    }
    @include media-breakpoint-up(md) {
      grid-area: pagination;
      position: static;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(bottom, 14);
    }
  }
  &__navigation {
    display: none;
    @include media-breakpoint-up(sm) {
      bottom: 0;
      display: flex;
      flex-direction: row;
      gap: 12px;
      position: absolute;
      padding: 0;
      right: 0px;
    }
    @include media-breakpoint-up(md) {
      align-self: flex-end;
      grid-area: navigation;
      position: static;
      justify-self: flex-end;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 12);
    }
  }
  &__image {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
    &--brand {
      height: auto;
      width: auto;
      max-width: 20%;
    }
    &-wrap {
      aspect-ratio: 240/144;
      align-items: center;
      display: flex;
      justify-content: center;
      overflow: hidden;
      width: 100%;
      @include media-breakpoint-up(sm) {
        aspect-ratio: 584/348;
      }
    }
  }
}
</style>
